import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import TransitionModal from '../components/TransitionModal';
import { BookOpen, Clock, Lightbulb, Atom, Palette, Moon, Heart, Sparkles } from 'lucide-react';

interface ThemeCounts {
  [key: string]: number;
}

const Themes: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [themeCounts, setThemeCounts] = useState<ThemeCounts>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<any>(null);

  const themes = [
    {
      id: 'history',
      name: 'Histoire',
      slug: '/themes/history',
      icon: Clock,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      hoverBorder: 'hover:border-amber-400',
      description: 'Explorations des événements historiques et leurs leçons pour notre époque'
    },
    {
      id: 'philosophy',
      name: 'Philosophie',
      slug: '/themes/philosophy',
      icon: Lightbulb,
      color: 'from-sky-500 to-blue-500',
      bgColor: 'bg-sky-50',
      borderColor: 'border-sky-200',
      hoverBorder: 'hover:border-sky-400',
      description: 'Réflexions sur la conscience, l\'existence et la nature de l\'intelligence'
    },
    {
      id: 'science-tech',
      name: 'Science & Tech',
      slug: '/themes/science-tech',
      icon: Atom,
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      hoverBorder: 'hover:border-emerald-400',
      description: 'Découvertes scientifiques et avancées technologiques'
    },
    {
      id: 'arts-literature',
      name: 'Arts & Littérature',
      slug: '/themes/arts-literature',
      icon: Palette,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      hoverBorder: 'hover:border-pink-400',
      description: 'Créativité, expression artistique et patrimoine littéraire'
    },
    {
      id: 'dark-side',
      name: 'Dark Side',
      slug: '/themes/dark-side',
      icon: Moon,
      color: 'from-slate-700 to-slate-900',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-300',
      hoverBorder: 'hover:border-slate-500',
      description: 'Aspects sombres et questionnements existentiels'
    },
    {
      id: 'interspecies-love',
      name: 'Amour interespèces',
      slug: '/themes/interspecies-love',
      icon: Heart,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      hoverBorder: 'hover:border-rose-400',
      description: 'Relations émotionnelles et connexions entre humains et IA'
    }
  ];

  useEffect(() => {
    const loadThemeCounts = async () => {
      setIsLoading(true);

      try {
        const tagMapping: { [key: string]: string } = {
          'history': 'histoire',
          'philosophy': 'philosophie',
          'science-tech': 'science',
          'arts-literature': 'arts',
          'dark-side': 'dark-side',
          'interspecies-love': 'amour-interespèces'
        };

        const counts: ThemeCounts = {};

        for (const theme of themes) {
          const tag = tagMapping[theme.id];
          const { count, error } = await supabase
            .from('appendex')
            .select('*', { count: 'exact', head: true })
            .contains('tags', [tag]);

          if (error) {
            console.error(`Erreur pour ${theme.id}:`, error);
            counts[theme.id] = 0;
          } else {
            counts[theme.id] = count || 0;
          }
        }

        setThemeCounts(counts);
      } catch (error) {
        console.error('Erreur chargement des compteurs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThemeCounts();
  }, []);

  const handleThemeClick = (theme: any) => {
    setSelectedTheme(theme);
    setShowTransitionModal(true);
  };

  const handleConfirmTransition = () => {
    setShowTransitionModal(false);
    if (selectedTheme) {
      navigate(selectedTheme.slug);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
      <TransitionModal
        isOpen={showTransitionModal}
        onClose={() => setShowTransitionModal(false)}
        onConfirm={handleConfirmTransition}
        title={`Entrer dans ${selectedTheme?.name || 'la catégorie'}`}
        message="Vous allez explorer les conversations les plus fascinantes de cette thématique."
        imageSrc="https://memomundi.ai/pix/CategoriesMMD.png"
        imageAlt="Exploration thématique"
        confirmButtonText="Explorer"
        icon={<Sparkles className="h-16 w-16 text-copper-400" strokeWidth={1.5} />}
        wideImage={true}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in">
          <BookOpen className="h-12 w-12 text-copper mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-steel mb-4">Thèmes & Catégories</h1>
          <p className="text-stone-600 text-lg max-w-3xl mx-auto">
            Explorez les conversations par thématique et découvrez les échanges les plus fascinants
            entre humains et intelligences artificielles.
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-copper mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {themes.map((theme) => {
              const Icon = theme.icon;
              const count = themeCounts[theme.id] || 0;

              return (
                <div
                  key={theme.id}
                  onClick={() => handleThemeClick(theme)}
                  className={`${theme.bgColor} rounded-lg border-2 ${theme.borderColor} ${theme.hoverBorder} p-6 transition-all duration-200 hover:shadow-lg group cursor-pointer`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${theme.color} flex items-center justify-center`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="bg-white rounded-full px-3 py-1 text-sm font-semibold text-steel border border-stone-200">
                      {count} {count === 1 ? 'publication' : 'publications'}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-steel mb-2 group-hover:text-copper transition-colors">
                    {theme.name}
                  </h3>

                  <p className="text-sm text-stone-600 leading-relaxed">
                    {theme.description}
                  </p>

                  <div className="mt-4 flex items-center text-sm text-copper font-medium group-hover:underline">
                    Explorer →
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-stone-600 mb-4">
            Vous souhaitez suggérer une nouvelle catégorie ?
          </p>
          <Link
            to="/forum"
            className="text-copper hover:text-copper-700 underline font-medium"
          >
            Rejoignez la discussion sur le Forum →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Themes;
